'use client';
//
// Realtime unread count for sidebar / bottom-tab badges.
//
// REWRITTEN 2026-05-05 for chat v2. The previous implementation
// queried two legacy schemas (`chats.teams[]` + `conversations.memberIds`)
// that no longer exist after the chat rebuild. This v2 version uses
// the single new schema:
//   - chats where participantIds array-contains <uid>
//   - per-chat /reads/{uid}.lastReadAt vs chat.lastMessageAt
//
// EXTENDED 2026-07-26 for unified SMS replies
// (features/unified-replies/): the count now also includes one unit per
// team with unread inbound SMS replies, fed by the shared smsReplyStore
// singleton. Same signature, same return shape — no call-site changes.
// SMS units are held back until the store settles (arch A-4) so badge
// behavior is bit-identical to chat-only until every initial snapshot
// has reported, and the pulse animation can't fire repeatedly at boot.
//
// Returns: { count, increased, source, perTeam, usingCounter }
//   - count: unread chats + teams with unread SMS replies
//   - increased: true if count just went up (drives ping animation)
//   - source: diagnostic string
//   - perTeam, usingCounter: kept for back-compat (always {})

import { useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import useChats from './useChats';
import useSmsReplyUnread from './useSmsReplyUnread';
import { combinedUnreadCount } from './smsReplyHelpers';

export default function useInboxUnreadCount(idOrIds, { scope = 'auto', teamId = null } = {}) {
  // The old API took team IDs or a uid as the first argument. In v2
  // we resolve unread counts purely against the signed-in user's
  // chat participantship, so we just use the auth uid. Args are
  // accepted (and ignored) for back-compat.
  const { firebaseUser } = useAuth();
  const uid = firebaseUser?.uid || null;

  const { chats } = useChats(uid);
  const sms = useSmsReplyUnread();

  const chatUnread = chats.filter((c) => c.isUnread).length;
  const count = combinedUnreadCount(chatUnread, sms.settled ? sms.units : 0);

  // "Increased" detection drives the pulse animation on the badge.
  // We compare current count to last seen count via a ref so we
  // don't re-render every tick. Suppressed until the SMS store has
  // settled so staggered initial snapshots can't fire pulse storms.
  const prevCountRef = useRef(0);
  const increased = sms.settled && count > prevCountRef.current;
  prevCountRef.current = count;

  return useMemo(
    () => ({
      count,
      increased,
      source: 'chat-v2+sms',
      perTeam: {},        // kept for back-compat
      usingCounter: {},   // kept for back-compat
    }),
    [count, increased]
  );
}
